import * as Sentry from '@sentry/react';
import { useMutation } from '@tanstack/react-query';
import { FC, Fragment, useRef, useState } from 'react';
import PhoneInput, { formatPhoneNumber, isValidPhoneNumber } from 'react-phone-number-input';
import flags from 'react-phone-number-input/flags';

import { useAppContext } from '@contexts/AppContext';

import API from '@api/API';

import {
  useCurrentEntertainerQuery,
  useFetchCurrentEntertainer,
} from '@queries/EntertainerQueries';
import { useCurrentUser } from '@queries/UserQueries';

import { useClaimEntertainerByInstagramHandleMutation } from '@mutations/EntertainerMutations';
import { useUpdateProfileMutation } from '@mutations/UserMutations';

import { useAnalytics } from '@hooks/useAnalytics';
import { useCitiesQuery } from '@hooks/useCurrentUserLocation';

import { openExternalUrl } from '@utilities/openExternalUrl';
import { sanitizeInstagramUsername } from '@utilities/sanitizeInstagramUsername';

import Button from '@components/buttons/Button';
import { FormLabel } from '@components/form/FormLabel';
import { Input } from '@components/form/Input';
import { Select } from '@components/form/Select';
import { useDialog } from '@components/layout/Dialog/useDialog';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

import { AuthPopupProps } from './LogInScreen';

interface AuthPopupUserOnboarding {
  firstName: string;
  lastName: string;
}

const UserOnboardingDetailsStep: FC<
  AuthPopupProps & {
    onSubmit: (userProperties: AuthPopupUserOnboarding) => void;
  }
> = ({ options, setIsEntertainerPopup, onSubmit }) => {
  const { cities = [] } = useCitiesQuery(!options?.cityId);
  const { track } = useAnalytics();
  const { openConfirmDialog } = useDialog();
  const [userProperties, setUpdatedUserProperties] = useState({
    firstName: '',
    lastName: '',
  });

  const handleContinue = () => {
    track('Light User Onboarding', {
      firstName: userProperties.firstName,
      lastName: userProperties.lastName,
    });
    onSubmit(userProperties);
  };

  return (
    <>
      <TitleToolbar
        text={`Step ${options?.authBeforeEntertainerSignup ? 2 : 1} of 2: Your Details`}
        size="md"
      />
      <div className="d-grid gap-2">
        <div>
          <FormLabel text="First Name *" />
          <Input
            name="firstName"
            onChange={(e) =>
              setUpdatedUserProperties((prevValue) => ({
                ...prevValue,
                firstName: e.target.value,
              }))
            }
          />
        </div>
        <div>
          <FormLabel text="Last Name *" />
          <Input
            type="text"
            name="lastName"
            onChange={(e) =>
              setUpdatedUserProperties((prevValue) => ({
                ...prevValue,
                lastName: e.target.value,
              }))
            }
          />
        </div>
        <div>
          <Button
            roundness="rounded"
            onClick={handleContinue}
            disabled={!userProperties.firstName || !userProperties.lastName}
          >
            Continue
          </Button>
        </div>
      </div>
    </>
  );
};

const PhoneNumberStep: FC<AuthPopupProps & { onSubmit: () => void }> = ({ onSubmit, ...props }) => {
  const {
    options,
    authMessage = 'For security, we require a phone number, but no worries, it stays confidential and is never shared.',
    phoneNumber,
    setPhoneNumber,
  } = props;

  return (
    <>
      <TitleToolbar
        text={`Step ${options?.authBeforeEntertainerSignup ? 1 : 2} of 2: Verification`}
        size="md"
      />
      <p className="mt-0">{authMessage}</p>
      <div className="d-grid gap-2">
        <div>
          <FormLabel text="Phone Number *" />
          <PhoneInput
            placeholder="(303) 555-1234"
            defaultCountry="US"
            flags={flags}
            value={phoneNumber}
            onChange={setPhoneNumber}
            inputComponent={Input}
          />
        </div>
        <div>
          <Button
            roundness="rounded"
            onClick={onSubmit}
            disabled={props.isAuthorizing || !isValidPhoneNumber(phoneNumber)}
          >
            Finish
          </Button>
        </div>
        <p className="text-center">
          {'By continuing, you agree to our '}
          <strong onClick={() => openExternalUrl('https://gaggl.app/terms')}>Terms</strong>
          {' and '}
          <strong onClick={() => openExternalUrl('https://gaggl.app/privacy-policy')}>
            Privacy Policy
          </strong>
          .
        </p>
      </div>
    </>
  );
};

export const UserOnboardingContent: FC<AuthPopupProps> = (props) => {
  const { currentUser } = useCurrentUser();
  const { userIsLoggedIn } = useAppContext();

  const { authBeforeEntertainerSignup = false } = props.options ?? {};

  const [step, setStep] = useState<number>(() => {
    let step = 0;

    if (authBeforeEntertainerSignup) {
      step = currentUser?._id || userIsLoggedIn ? 1 : 0;
    }

    return step;
  });

  const handleSubmitFirstStep = async (userProperties: AuthPopupUserOnboarding) => {
    try {
      setStep((prevStep) => prevStep + 1);

      props.setOnboardingUserProperties(userProperties);
    } catch (e) {
      Sentry.captureException(e);
    }
  };

  const handleSumbitSecondStep = async () => {
    try {
      await props.handleInternalPhoneLogIn();
    } catch (e) {
      Sentry.captureException(e);
    }
  };

  const STEPS = {
    user: {
      key: 'user',
      render: () => <UserOnboardingDetailsStep {...props} onSubmit={handleSubmitFirstStep} />,
    },
    phoneNumber: {
      key: 'phoneNumber',
      render: () => <PhoneNumberStep {...props} onSubmit={handleSumbitSecondStep} />,
    },
  };

  const steps = [STEPS.user, STEPS.phoneNumber];

  return <div>{steps[step]?.render?.()}</div>;
};
